/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomDatePickerV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Box, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
const CustomDatePickerV2 = ({
  label = "Select Date",
  value,
  onChange,
  views = ["year"],
  required = false,
  disabled = false,
  fullWidth = true,

  txtFldStyle = {},
}) => {
  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#d9d9d9", borderRadius: "0px" }, // Default
      "&:hover fieldset": { borderColor: "#8c8c8c" }, // Hover
      "&.Mui-focused fieldset": { borderColor: "#000000" }, // Focused
      "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" }, // Disabled
      "&.Mui-error fieldset": { borderColor: "#e53935" }, // Error

      // 🔹 Change text color on hover
      "&:hover .MuiInputBase-input::placeholder": {
        color: "#848587", // dark text color on hover
      },

      // 🔹 Change text color on focus (selected)
      "&.Mui-focused .MuiInputBase-input::placeholder": {
        color: "#232323", // solid black when focused
      },
    },

    "& .MuiInputLabel-root": {
      color: "#000",
      fontSize: "0.9rem",
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      fontSize: "0.8rem",
      marginLeft: 0,
      marginTop: "4px",
    },
    ...txtFldStyle,
  };
  return (
    <Box sx={styles.container}>
      <Box sx={styles.titleBox}>
        <Typography
          variant="subtitle1"
          sx={{
            color: "#6f7174",
            fontSize: "0.9rem",
            fontWeight: 500,
          }}
        >
          {label} <span style={{ color: "red" }}>{required ? "*" : ""}</span>
        </Typography>
      </Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ width: fullWidth ? "100%" : "auto", ...textFieldStyle }}>
          <DatePicker
            value={value}
            onChange={onChange}
            views={views}
            disabled={disabled}
            slots={{
              openPickerIcon: CalendarMonthIcon,
            }}
            slotProps={{
              textField: {
                variant: "outlined",
                fullWidth: true,
                size: "small",
                sx: {
                  "& .MuiOutlinedInput-root": { borderRadius: 0 },
                  "& .MuiSvgIcon-root": { color: "#000" }, // black icon
                },
              },
              popper: {
                sx: {
                  // 🔥 SELECTED YEAR
                  "& .MuiPickersYear-yearButton.Mui-selected": {
                    backgroundColor: "#000 !important",
                    color: "#fff !important",
                    borderRadius: 0,
                  },

                  "& .MuiPickersYear-yearButton.Mui-selected:hover": {
                    backgroundColor: "#000",
                  },

                  // 🔥 SELECTED MONTH
                  "& .MuiPickersMonth-monthButton.Mui-selected": {
                    backgroundColor: "#000 !important",
                    color: "#fff !important",
                    borderRadius: 0,
                  },

                  "& .MuiPickersMonth-monthButton.Mui-selected:hover": {
                    backgroundColor: "#000",
                  },
                },
              },
            }}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );
};

export default CustomDatePickerV2;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  titleBox: {
    ml: 1,
  },
  chip: {
    backgroundColor: "#53565A",
    color: "white",
    borderRadius: 0,
    "& .MuiChip-deleteIcon": {
      color: "white",
      fontSize: 18,
      "&:hover": {
        color: "red",
      },
    },
  },
};
